// The map: a road the enemy walks, from a gate at the edge of the map to the Command Center at its
// end, and the ground either side of it where the towers go. There is one road; the difficulty
// sets how forgiving the game on it is (starting cash, the Command Center's integrity, how many
// waves make a win, how tough the enemy is). A `TdMap` is the road at one difficulty. The road is
// rasterised to a tile mask once, so placement and the renderer agree on which tiles are road. The
// sea is off the west edge of the map (grid.ts); the enemy's fleet holds station out on it (td.ts).
import { MAP_SIZE, TILE, isqrt } from './fixed.ts';

/** How wide the road is, in tiles: a tile whose centre lies within half this of the centre line is road. */
export const ROAD_WIDTH = 2;
/** The Command Center's footprint, in tiles (it is `hq` in data.ts, size 4). */
export const HQ_SIZE = 4;

export type Difficulty = 'schmuck' | 'joe' | 'defcon1';
export const DIFFICULTIES: readonly Difficulty[] = ['schmuck', 'joe', 'defcon1'];
/** The ids the difficulties went by before they had names, so a record or a saved game from then still finds its map. */
export const LEGACY_IDS: Record<string, Difficulty> = { easy: 'schmuck', medium: 'joe', hard: 'defcon1', shmuck: 'schmuck' };

/**
 * The one map at one difficulty: the road is the same at every level, and the level sets how
 * forgiving the rest is. `id` is the difficulty, so a record and a saved game are kept by it.
 */
export interface TdMap {
  id: Difficulty;
  /** The difficulty's name: Schmuck, Average Joe, DEFCON 1. */
  name: string;
  /** One line on what this level asks of the player. */
  blurb: string;
  difficulty: Difficulty;
  /** The road's corners in tiles, from where the enemy enters (beyond the map edge) to the Command Center's centre. */
  road: [number, number][];
  /** A second road in, ending at the same place: the enemy splits between the two. */
  road2?: [number, number][];
  /** Cash in hand at the start. */
  cash: number;
  /** The Command Center's integrity: what the enemy must wear down to win. */
  integrity: number;
  /** Waves to hold for the win; the game goes on past it, endless, for the record. */
  waves: number;
  /** The enemy's hit points, in percent of the unit's own, on top of what the wave number adds. */
  armourPct: number;
  /** The enemy goes nuclear: ICBMs off a submarine from the tenth wave and nuclear glide vehicles past the twentieth (DEFCON 1 only). */
  nuclear: boolean;
  /** How many of each group come, in percent of the wave's book: a harder level sends more, closer together. */
  countPct?: number;
  /** How fast the enemy moves, in percent of its own speed. */
  speedPct?: number;
  /** What a kill pays, in percent. */
  payPct?: number;
}

/** The gates in the countryside (outskirts3d.ts): the road comes in through one, so it meets the highway there. */
export const GATE = {
  east: { x: MAP_SIZE, y: 22 },
  north: { x: 42, y: 0 },
  south: { x: 20, y: MAP_SIZE },
} as const;

/** How far beyond the edge the road starts: the enemy walks in from the countryside. */
const APPROACH = 9;

/** Every road a map has, the main one first. */
export function roadsOf(map: TdMap): [number, number][][] {
  return map.road2 ? [map.road, map.road2] : [map.road];
}

/**
 * The road: in from the south gate, once round the whole plan and then in to the Command Center.
 * A gun on a corner covers two legs at once.
 */
export const ROAD: [number, number][] = [
  [GATE.south.x + 0.5, GATE.south.y + APPROACH],
  [GATE.south.x + 0.5, 44.5],
  [46.5, 44.5],
  [46.5, 14.5],
  [14.5, 14.5],
  [14.5, 30.5],
  [34.5, 30.5],
];

/** The map at each difficulty, easiest first. */
export const MAPS: TdMap[] = [
  {
    id: 'schmuck', name: 'Schmuck', difficulty: 'schmuck',
    blurb: 'Thirty waves, a full war chest and a Command Center that takes a beating. The place to learn what each gun is for.',
    road: ROAD, cash: 650, integrity: 150, waves: 30, armourPct: 100, nuclear: false,
  },
  {
    id: 'joe', name: 'Average Joe', difficulty: 'joe',
    blurb: 'Forty waves, less cash, and every enemy wears a little more armour. The shield has to go up on time.',
    road: ROAD, cash: 600, integrity: 120, waves: 40, armourPct: 115, nuclear: false,
  },
  {
    id: 'defcon1', name: 'DEFCON 1', difficulty: 'defcon1',
    blurb: 'Fifty waves of a war: half as many again in every wave, faster, armoured to the teeth, poorly paid, against a Command Center that cannot take much. From the tenth wave a submarine offshore fires ICBMs; past the twentieth the glide vehicles are nuclear too. Only THAAD reaches them.',
    road: ROAD, cash: 550, integrity: 80, waves: 50, armourPct: 170, nuclear: true, countPct: 150, speedPct: 115, payPct: 75,
  },
];

/** The map at a difficulty; undefined for an id that is not one (a record or save from before there was one map). */
export function mapById(id: string): TdMap | undefined {
  const want = LEGACY_IDS[id] ?? id;
  return MAPS.find(m => m.id === want);
}

/** One road as the sim walks it: corners in units, and the distance along it at each. */
export interface Lane {
  pts: [number, number][];
  at: number[];
  length: number;
}

/** The map's roads as the sim walks them, and the tiles they cover. `pts`, `at` and `length` are the main road's. */
export interface Road extends Lane {
  /** The roads, the main one first. */
  lanes: Lane[];
  /** One byte per tile: 1 where the road is. */
  mask: Uint8Array;
  /** The Command Center's top-left tile. */
  hqX: number;
  hqY: number;
}

/** Distance from a point to the nearest point of a segment, in fractional tiles (presentation and placement only). */
function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

function buildLane(corners: [number, number][]): Lane {
  const pts: [number, number][] = corners.map(([x, y]) => [Math.round(x * TILE), Math.round(y * TILE)]);
  const at: number[] = [0];
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1]!, [bx, by] = pts[i]!;
    at.push(at[i - 1]! + isqrt((bx - ax) ** 2 + (by - ay) ** 2));
  }
  return { pts, at, length: at[at.length - 1]! };
}

export function buildRoad(map: TdMap): Road {
  const lanes = roadsOf(map).map(buildLane);
  const mask = new Uint8Array(MAP_SIZE * MAP_SIZE);
  const half = ROAD_WIDTH / 2;
  for (let y = 0; y < MAP_SIZE; y++) {
    for (let x = 0; x < MAP_SIZE; x++) {
      const cx = x + 0.5, cy = y + 0.5;
      road: for (const corners of roadsOf(map)) {
        for (let i = 1; i < corners.length; i++) {
          const [ax, ay] = corners[i - 1]!, [bx, by] = corners[i]!;
          if (segDist(cx, cy, ax, ay, bx, by) <= half) { mask[y * MAP_SIZE + x] = 1; break road; }
        }
      }
    }
  }
  const [ex, ey] = map.road[map.road.length - 1]!;
  const hqX = Math.round(ex - HQ_SIZE / 2), hqY = Math.round(ey - HQ_SIZE / 2);
  // the Command Center's own tiles are not road: nothing is built there anyway, and the road is drawn up to its door
  for (let dy = 0; dy < HQ_SIZE; dy++) for (let dx = 0; dx < HQ_SIZE; dx++) {
    const x = hqX + dx, y = hqY + dy;
    if (x >= 0 && y >= 0 && x < MAP_SIZE && y < MAP_SIZE) mask[y * MAP_SIZE + x] = 0;
  }
  const main = lanes[0]!;
  return { ...main, lanes, mask, hqX, hqY };
}

/** Where a thing `s` units along a road is, in units, and the leg it is on. Integer. */
export function roadPoint(road: Road, s: number, lane = 0): [x: number, y: number, leg: number] {
  const { pts, at } = road.lanes[lane] ?? road;
  if (s <= 0) return [pts[0]![0], pts[0]![1], 0];
  if (s >= at[at.length - 1]!) { const p = pts[pts.length - 1]!; return [p[0], p[1], pts.length - 2]; }
  let i = 1;
  while (i < at.length - 1 && at[i]! <= s) i++;
  const [ax, ay] = pts[i - 1]!, [bx, by] = pts[i]!;
  const len = at[i]! - at[i - 1]!;
  const k = s - at[i - 1]!;
  return [ax + Math.trunc(((bx - ax) * k) / len), ay + Math.trunc(((by - ay) * k) / len), i - 1];
}

/** The unit direction of a leg, in thousandths, so a thing can be set off to the side of the road. */
export function legDir(road: Road, leg: number, lane = 0): [dx: number, dy: number] {
  const l = road.lanes[lane] ?? road;
  const i = Math.max(0, Math.min(l.pts.length - 2, leg));
  const [ax, ay] = l.pts[i]!, [bx, by] = l.pts[i + 1]!;
  const len = l.at[i + 1]! - l.at[i]!;
  if (len === 0) return [1000, 0];
  return [Math.trunc(((bx - ax) * 1000) / len), Math.trunc(((by - ay) * 1000) / len)];
}
